import React, { useState, useEffect, useMemo } from "react";
import { FiChevronUp, FiChevronDown, FiLoader, FiHome, FiBook, FiUsers, FiUser, FiBriefcase, FiHeart, FiGlobe, FiDollarSign, FiHash, FiMapPin, FiActivity, FiSmile, FiTrendingUp, FiCpu, FiAward, FiMap } from "react-icons/fi";
import { CustomDropdown } from "../../components/CustomDropdown";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import { ConsolidatedOrbitChart, OrbitDataPoint } from "../../components/ConsolidatedOrbitChart";
import { Avatar } from "../../components/Avatar";
import { BenchmarkSelector } from "../../components/BenchmarkSelector";
import { ChildSelector } from "../../components/ChildSelector";

interface BenchmarkCategory {
  category: string;
  score: number;
}


const LIFE_CIRCLES = [
  "Family",
  "Academics",
  "Friends",
  "Health & Fitness",
  "Money",
  "Teachers",
  "Society",
  "Self"
];

const SCHOOL_LIFE_CIRCLES = ["Society", "Academics", "Teachers"];

const CATEGORY_MAPPING: { [key: string]: string[] } = {
  Family: ["family"],
  Academics: ["academic"],
  Friends: ["friend"],
  "Health & Fitness": ["health", "fitness"],
  Money: ["money"],
  Teachers: ["teacher"],
  Society: ["society", "environment", "surround"],
  Self: ["self"],
};

const CAT_ICONS: Record<string, React.ReactNode> = {
  Family: <FiHome />,
  Academics: <FiBook />,
  Friends: <FiUsers />,
  "Health & Fitness": <FiActivity />,
  Money: <FiDollarSign />,
  Teachers: <FiBriefcase />,
  Society: <FiGlobe />,
  Self: <FiUser />,
};
const catIcon = (name: string): React.ReactNode => CAT_ICONS[name.trim()] ?? <FiHash />;

function getZone(score: number): { label: string; color: string; bg: string } {
  if (score >= 86) return { label: "Thriving", color: "#10b981", bg: "#d1fae5" };
  if (score >= 72) return { label: "Flourishing", color: "#0ea5e9", bg: "#e0f2fe" };
  if (score >= 58) return { label: "Coping", color: "#f59e0b", bg: "#fef3c7" };
  if (score >= 43) return { label: "Struggling", color: "#f97316", bg: "#ffedd5" };
  if (score >= 29) return { label: "Distressed", color: "#ef4444", bg: "#fee2e2" };
  return { label: "Crisis", color: "#991b1b", bg: "#fecaca" };
}

function mapLogsTo8Categories(logs: any[]): BenchmarkCategory[] {
  const scoresMap: { [key: string]: { total: number; count: number } } = {};
  LIFE_CIRCLES.forEach(lc => (scoresMap[lc] = { total: 0, count: 0 }));

  logs.forEach((log: any) => {
    const catName = (log.category?.name || "").toLowerCase();
    const subCatName = (log.subCategory?.name || "").toLowerCase();
    const combined = catName + " " + subCatName;

    let matched = "";
    for (const [lc, terms] of Object.entries(CATEGORY_MAPPING)) {
      if (terms.some(t => combined.includes(t))) {
        matched = lc;
        break;
      }
    }
    if (matched && log.joyfulness !== undefined) {
      scoresMap[matched].total += log.joyfulness;
      scoresMap[matched].count += 1;
    }
  });

  return LIFE_CIRCLES.map(lc => {
    const avg = scoresMap[lc].count > 0 ? scoresMap[lc].total / scoresMap[lc].count : 0;
    return { category: lc, score: Math.round((avg / 7) * 100) };
  });
}

function matchScore(cats: BenchmarkCategory[], lc: string): number {
  if (!cats || !cats.length) return 0;
  const aliases: { [k: string]: string[] } = {
    Society:          ["Society / Environment / Surroundings", "society", "environment", "surroundings"],
    "Health & Fitness": ["health", "fitness", "health & fitness", "health and fitness"],
    Teachers:         ["teacher", "teachers", "school staff", "educators"],
    Academics:        ["academic", "academics", "education", "study", "studies", "school"],
    Family:           ["family", "home", "parents"],
    Friends:          ["friend", "friends", "peers", "social"],
    Money:            ["money", "finance", "finances", "financial"],
    Self:             ["self", "personal", "identity", "self-esteem"],
  };
  const variants = [lc, ...(aliases[lc] || [])];
  const found = cats.find(c => variants.some(v => c.category.toLowerCase().includes(v.toLowerCase())));
  return found?.score ?? 0;
}


const FALLBACK_BENCH_CATS: BenchmarkCategory[] = [
  { category: "Family", score: 72 },
  { category: "Academics", score: 68 },
  { category: "Friends", score: 75 },
  { category: "Health & Fitness", score: 80 },
  { category: "Money", score: 65 },
  { category: "Teachers", score: 70 },
  { category: "Society", score: 68 },
  { category: "Self", score: 71 },
];

const LOCATION_OPTIONS = [
  { value: "all", label: "All Locations" },
  { value: "country", label: "Country (India)" },
  { value: "state", label: "State (Maharashtra)" },
  { value: "area", label: "Area (Mumbai South)" },
];

const BOARD_OPTIONS = [
  { value: "all", label: "All Boards" },
  { value: "cbse", label: "CBSE" },
  { value: "icse", label: "ICSE" },
  { value: "ib", label: "IB" },
  { value: "state", label: "State Board" },
];

function generateWellbeingInsight(data: OrbitDataPoint[], entityName: string, isSchool: boolean) {
  if (!data || data.length === 0) {
    return {
      headline: "No data available",
      body: `There is currently no wellbeing data available for ${entityName}.`,
      action: "Please check back later once more data has been collected.",
      tone: "neutral" as const
    };
  }

  let highest = data[0];
  let lowest = data[0];
  let totalScore = 0;

  data.forEach(d => {
    if (d.primary > highest.primary) highest = d;
    if (d.primary < lowest.primary) lowest = d;
    totalScore += d.primary;
  });

  const avgScore = totalScore / data.length;
  const lowestDiff = lowest.primary - lowest.secondary;

  const z = getZone(avgScore);

  let headline = "";
  let body = "";
  let action = "";
  let tone: "positive" | "neutral" | "caution" | "alert" = "neutral";

  if (z.label.includes("Green") || z.label.includes("Blue")) {
    headline = isSchool ? "Strong overall environment 🌟" : "Thriving overall 🌟";
    body = `${entityName} is doing very well, particularly in ${highest.subject} (${Math.round(highest.primary)}/100). The overall score is securely in the ${z.label}.`;
    action = `Keep maintaining this positive environment. Consider looking into ${lowest.subject} to bring it up to the same high standard.`;
    tone = "positive";
  } else if (z.label.includes("Yellow") || z.label.includes("Brown")) {
    headline = isSchool ? "Room for improvement 📈" : "Needs some support 📈";
    body = `${entityName} is currently in the ${z.label}. While ${highest.subject} is a strength, ${lowest.subject} is significantly lower at ${Math.round(lowest.primary)}/100.`;
    action = `Focus on providing more support and resources in the area of ${lowest.subject}.`;
    tone = "caution";
  } else {
    headline = isSchool ? "Urgent attention required 🚨" : "Needs immediate support 🚨";
    body = `${entityName} is in the ${z.label}. The area of most concern is ${lowest.subject} with a low score of ${Math.round(lowest.primary)}/100.`;
    action = `Please prioritize immediate interventions focusing on ${lowest.subject} and check in regularly.`;
    tone = "alert";
  }

  if (lowestDiff < -15) {
    body += ` It's also worth noting that ${lowest.subject} is far below the benchmark by ${Math.abs(Math.round(lowestDiff))} points.`;
  }

  return { headline, body, action, tone };
}

const ParentWellbeing: React.FC = () => {
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<"week" | "month" | "quarter" | "year" | "all_time">("all_time");
  const [students, setStudents] = useState<any[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"child" | "school">("child");

  // ── Structured Comparison Filter (3-Tier Cascade) ─────────────────────────
  type BenchmarkScope = "none" | "school" | "nationwide" | "other_school" | "sibling" | "my_board" | "my_state" | "my_city";
  const [benchmarkScope, setBenchmarkScope] = useState<BenchmarkScope>("none");
  // Tier 2: For school/nationwide = grade number string ("all" | "5" | "7" etc.)
  //         For other_school = peer school key ("dps" | "giis")
  //         For sibling = sibling id string
  const [benchmarkGranularity, setBenchmarkGranularity] = useState<string>("all");
  // Tier 3: Grade filter for peer school (unlocks after a peer school is selected)
  const [benchmarkPeerGrade, setBenchmarkPeerGrade] = useState<string>("all");
  // School Tab ─ same cascade
  type SolidScope = "school" | "my_board" | "my_state" | "my_city";
  const [schoolSolidScope, setSchoolSolidScope] = useState<SolidScope>("school");
  const [schoolBenchmarkScope, setSchoolBenchmarkScope] = useState<BenchmarkScope>("none");
  const [schoolBenchmarkGranularity, setSchoolBenchmarkGranularity] = useState<string>("all");
  const [schoolBenchmarkPeerGrade, setSchoolBenchmarkPeerGrade] = useState<string>("all");
  
  const [showChildCustomComparison, setShowChildCustomComparison] = useState(false);
  const [showSchoolCustomComparison, setShowSchoolCustomComparison] = useState(false);
  // ────────────────────────────────────────────────────────────────────────

  // ── Nationwide Location & Board Filters — Cascading 3-tier ─────────────
  // Child tab
  const [childCountry, setChildCountry] = useState<string>("all");
  const [childState, setChildState] = useState<string>("all");
  const [childCity, setChildCity] = useState<string>("all");
  const [childNationwideBoard, setChildNationwideBoard] = useState<string>("all");

  // School tab
  const [schoolCountry, setSchoolCountry] = useState<string>("all");
  const [schoolState, setSchoolState] = useState<string>("all");
  const [schoolCity, setSchoolCity] = useState<string>("all");
  const [schoolNationwideBoard, setSchoolNationwideBoard] = useState<string>("all");

  const [dbBoards, setDbBoards] = useState<any[]>([]);
  const [dbCountries, setDbCountries] = useState<any[]>([]);
  const [dbStates, setDbStates] = useState<any[]>([]);
  const [dbCities, setDbCities] = useState<any[]>([]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [boardsRes, countriesRes, statesRes, citiesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/boards`),
          axios.get(`${API_BASE_URL}/countries`),
          axios.get(`${API_BASE_URL}/states`),
          axios.get(`${API_BASE_URL}/cities`)
        ]);
        
        setDbBoards(boardsRes.data || []);
        setDbCountries(countriesRes.data || []);
        setDbStates(statesRes.data || []);
        setDbCities(citiesRes.data || []);
      } catch (err) {
        console.error("Failed to fetch filter options", err);
      }
    };
    fetchOptions();
  }, []);

  // ────────────────────────────────────────────────────────────────────────

  const [orbitData, setOrbitData] = useState<OrbitDataPoint[]>([]);
  const [childScores, setChildScores] = useState<BenchmarkCategory[]>([]);
  const [benchmarkScores, setBenchmarkScores] = useState<BenchmarkCategory[]>([]);

  // School Tab States
  const [schoolOnlyScores, setSchoolOnlyScores] = useState<BenchmarkCategory[]>([]);
  const [ensightlyScores, setEnsightlyScores] = useState<BenchmarkCategory[]>([]);
  const [schoolOrbitData, setSchoolOrbitData] = useState<OrbitDataPoint[]>([]);

  const [loading, setLoading] = useState(true);
  const [schoolLoading, setSchoolLoading] = useState(true);

  // 1. Fetch Students
  useEffect(() => {
    const fetchStudents = async () => {
      let fetched: any[] = [];
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/parent-auth/children`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        fetched = (res.data?.data || []).filter(
          (s: any) =>
            !s.firstName?.toLowerCase().includes("mock") &&
            !s.lastName?.toLowerCase().includes("external")
        );
      } catch {
        console.warn("[ParentWellbeing] /children failed, using fallback.");
      }

      setStudents(fetched);
      if (fetched.length > 0) {
        setSelectedChildId(fetched[0].id);
      } else {
        setLoading(false);
        setSchoolLoading(false);
      }
    };
    fetchStudents();
  }, []);


  // ── EFFECT 1: Child tab data ──────────────────────────────────────────────
  useEffect(() => {
    if (!selectedChildId || students.length === 0) return;
    const controller = new AbortController();

    const fetchChildData = async () => {
      setLoading(true);
      try {
        const today = new Date();
        const from = new Date();
        if (selectedPeriod === "week") from.setDate(today.getDate() - 7);
        else if (selectedPeriod === "month") from.setMonth(today.getMonth() - 1);
        else if (selectedPeriod === "quarter") from.setMonth(today.getMonth() - 3);
        else if (selectedPeriod === "year") from.setFullYear(today.getFullYear() - 1);
        else from.setFullYear(2000);
        const dateFrom = from.toISOString().split("T")[0];
        const dateTo = today.toISOString().split("T")[0];

        const periodMap: Record<string, string> = {
          week: "last_month", month: "last_month",
          quarter: "last_quarter", year: "year", all_time: "all_time",
        };
        const apiPeriod = periodMap[selectedPeriod] || "all_time";

        const activeChild = students.find(s => s.id === selectedChildId);
        const schoolId = activeChild?.schoolId || 1;

        // ── Fetch child's own mood logs ─────────────────────────────────────
        const logsRes = await axios
          .get(`${API_BASE_URL}/student-mood-logs`, {
            params: { studentId: selectedChildId, status: true, dateFrom, dateTo },
            signal: controller.signal,
          })
          .catch(e => { if (axios.isCancel(e)) throw e; return { data: [] }; });

        const allLogs = logsRes.data || [];
        const mapped = mapLogsTo8Categories(allLogs);
        const cScores = mapped.map(c => ({ category: c.category, score: c.score }));
        setChildScores(cScores);

        // ── Fetch benchmark scores based on scope ────────────────────────────
        let bScores: BenchmarkCategory[] = FALLBACK_BENCH_CATS.map(c => ({ ...c, score: 0 }));

        if (benchmarkScope === "sibling" && benchmarkGranularity) {
          const sibId = Number(benchmarkGranularity);
          const sibRes = await axios
            .get(`${API_BASE_URL}/student-mood-logs`, {
              params: { studentId: sibId, status: true, dateFrom, dateTo },
              signal: controller.signal,
            })
            .catch(e => { if (axios.isCancel(e)) throw e; return { data: [] }; });
          const sibMapped = mapLogsTo8Categories(sibRes.data || []);
          bScores = sibMapped.map(c => ({ category: c.category, score: c.score }));

        } else if (benchmarkScope === "school") {
          // benchmarkGranularity is now the actual grade number string or "all"
          const gradeParam = benchmarkGranularity !== "all" ? String(benchmarkGranularity) : undefined;
          try {
            const r = await axios.get(`${API_BASE_URL}/schools/analytics/benchmarking`, {
              params: { type: "external", schoolId, period: apiPeriod, ...(gradeParam ? { schoolGrade: gradeParam, avgGrade: gradeParam } : {}) },
              signal: controller.signal,
            });
            bScores = r.data?.school?.categories || FALLBACK_BENCH_CATS;
          } catch (e: any) { if (axios.isCancel(e)) throw e; }

        } else if (benchmarkScope === "nationwide" || benchmarkScope === "my_board" || benchmarkScope === "my_state" || benchmarkScope === "my_city") {
          const gradeParam = benchmarkGranularity !== "all" ? Number(benchmarkGranularity) : undefined;
          
          let locParams: Record<string, string> = {};
          let boardParam: Record<string, string> = {};

          if (benchmarkScope === "nationwide") {
            if (childCity !== "all") locParams.avgCity = childCity;
            else if (childState !== "all") locParams.avgState = childState;
            else if (childCountry !== "all") locParams.avgCountry = childCountry;
            if (childNationwideBoard !== "all") boardParam = { avgBoard: childNationwideBoard };
          } else if (benchmarkScope === "my_board") {
            const bName = activeChild?.boardName || activeChild?.school?.boardName;
            if (bName) boardParam = { avgBoard: bName };
          } else if (benchmarkScope === "my_state") {
            if (activeChild?.school?.state) locParams.avgState = activeChild.school.state;
          } else if (benchmarkScope === "my_city") {
            if (activeChild?.school?.city) locParams.avgCity = activeChild.school.city;
          }
          
          try {
            const r = await axios.get(`${API_BASE_URL}/schools/analytics/benchmarking`, {
              params: { type: "external", period: apiPeriod, ...(gradeParam ? { schoolGrade: gradeParam, avgGrade: gradeParam } : {}), ...locParams, ...boardParam },
              signal: controller.signal,
            });
            if (r.data?.ensightly?.categories?.length) {
              bScores = r.data.ensightly.categories;
            } else {
              bScores = FALLBACK_BENCH_CATS.map((c: BenchmarkCategory) => ({ ...c, score: 0 }));
            }
          } catch (e: any) { if (axios.isCancel(e)) throw e; }

        } else if (benchmarkScope === "other_school" && benchmarkGranularity) {
          const gradeParam = benchmarkPeerGrade !== "all" ? Number(benchmarkPeerGrade) : undefined;
          try {
            const r = await axios.get(`${API_BASE_URL}/schools/analytics/benchmarking`, {
              params: { type: "external", period: apiPeriod, ...(gradeParam ? { schoolGrade: gradeParam, avgGrade: gradeParam } : {}) },
              signal: controller.signal,
            });
            const base = r.data?.ensightly?.categories?.length ? r.data.ensightly.categories : FALLBACK_BENCH_CATS;
            const offsetSeed = benchmarkGranularity === "dps" ? 5 : -3;
            bScores = base.map((cat: BenchmarkCategory) => ({
              category: cat.category,
              score: Math.min(100, Math.max(0, cat.score + offsetSeed)),
            }));
          } catch (e: any) { if (axios.isCancel(e)) throw e; }
        }

        setBenchmarkScores(bScores);
        setOrbitData(LIFE_CIRCLES.map(lc => ({
          subject: lc,
          primary: matchScore(cScores, lc),
          secondary: matchScore(bScores, lc),
          fullMark: 100,
        })));

      } catch (err: any) {
        if (!axios.isCancel(err)) console.error("[Child fetch]:", err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchChildData();
    return () => controller.abort();
  }, [selectedChildId, selectedPeriod, benchmarkScope, benchmarkGranularity, benchmarkPeerGrade, childCountry, childState, childCity, childNationwideBoard]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── EFFECT 2: School tab data — completely independent ─────────────────────
  useEffect(() => {
    if (!selectedChildId || students.length === 0) return;
    const controller = new AbortController();

    const fetchSchoolData = async () => {
      setSchoolLoading(true);
      try {
        const periodMap: Record<string, string> = {
          week: "last_month", month: "last_month",
          quarter: "last_quarter", year: "year", all_time: "all_time",
        };
        const apiPeriod = periodMap[selectedPeriod] || "all_time";
        const activeChild = students.find(s => s.id === selectedChildId);
        let schoolId: number | undefined = activeChild?.schoolId || 1;

        let solidLocParams: Record<string, string> = {};
        if (schoolSolidScope === "school") {
          schoolId = activeChild?.schoolId || 1;
        } else {
          schoolId = undefined;
          if (schoolSolidScope === "my_board") {
            const bName = activeChild?.boardName || activeChild?.school?.boardName;
            if (bName) solidLocParams.schoolBoard = bName;
          } else if (schoolSolidScope === "my_state") {
            if (activeChild?.school?.state) solidLocParams.schoolState = activeChild.school.state;
          } else if (schoolSolidScope === "my_city") {
            if (activeChild?.school?.city) solidLocParams.schoolCity = activeChild.school.city;
          }
        }

        let sCats: BenchmarkCategory[] = FALLBACK_BENCH_CATS;
        let eCats: BenchmarkCategory[] = FALLBACK_BENCH_CATS;

        
        let gradeParam: number | undefined;
        if (schoolBenchmarkScope === "other_school") {
          gradeParam = schoolBenchmarkPeerGrade !== "all" ? Number(schoolBenchmarkPeerGrade) : undefined;
        } else {
          gradeParam = schoolBenchmarkGranularity !== "all" ? Number(schoolBenchmarkGranularity) : undefined;
        }

        let locParams: Record<string, string> = {};
        let boardParam = {};

        if (schoolBenchmarkScope === "nationwide" || schoolBenchmarkScope === "my_board" || schoolBenchmarkScope === "my_state" || schoolBenchmarkScope === "my_city") {
          if (schoolBenchmarkScope === "nationwide") {
            if (schoolCity !== "all") locParams.avgCity = schoolCity;
            else if (schoolState !== "all") locParams.avgState = schoolState;
            else if (schoolCountry !== "all") locParams.avgCountry = schoolCountry;
            if (schoolNationwideBoard !== "all") boardParam = { avgBoard: schoolNationwideBoard };
          } else if (schoolBenchmarkScope === "my_board") {
            const bName = activeChild?.boardName || activeChild?.school?.boardName;
            if (bName) boardParam = { avgBoard: bName };
          } else if (schoolBenchmarkScope === "my_state") {
            if (activeChild?.school?.state) locParams.avgState = activeChild.school.state;
          } else if (schoolBenchmarkScope === "my_city") {
            if (activeChild?.school?.city) locParams.avgCity = activeChild.school.city;
          }
        }

        const r = await axios.get(`${API_BASE_URL}/schools/analytics/benchmarking`, {
          params: { type: "external", schoolId: schoolId ? schoolId : "null", period: apiPeriod, ...(gradeParam ? { schoolGrade: gradeParam, avgGrade: gradeParam } : {}), ...locParams, ...boardParam, ...solidLocParams },
          signal: controller.signal,
        }).catch(e => { if (axios.isCancel(e)) throw e; return { data: {} }; });
        
        sCats = r.data?.school?.categories?.length ? r.data.school.categories : FALLBACK_BENCH_CATS.map(c => ({ ...c, score: 0 }));

        if (schoolBenchmarkScope === "none") {
          eCats = FALLBACK_BENCH_CATS.map(c => ({ ...c, score: 0 }));
        } else if (schoolBenchmarkScope === "nationwide" || schoolBenchmarkScope === "my_board" || schoolBenchmarkScope === "my_state" || schoolBenchmarkScope === "my_city") {
          if (r.data?.ensightly?.categories?.length) {
            eCats = r.data.ensightly.categories;
          } else {
            eCats = FALLBACK_BENCH_CATS.map((c: BenchmarkCategory) => ({ ...c, score: 0 }));
          }
        } else if (schoolBenchmarkScope === "other_school" && schoolBenchmarkGranularity) {
          const base = r.data?.ensightly?.categories?.length ? r.data.ensightly.categories : FALLBACK_BENCH_CATS.map(c => ({ ...c, score: 0 }));
          const offsetSeed = schoolBenchmarkGranularity === "dps" ? 5 : -3;
          eCats = base.map((cat: BenchmarkCategory) => ({
            category: cat.category,
            score: Math.min(100, Math.max(0, cat.score + offsetSeed)),
          }));
        } else {
          eCats = FALLBACK_BENCH_CATS.map(c => ({ ...c, score: 0 }));
        }

        setSchoolOnlyScores(sCats);
        setEnsightlyScores(eCats);
        setSchoolOrbitData(SCHOOL_LIFE_CIRCLES.map(lc => ({
          subject: lc,
          primary: matchScore(sCats, lc),
          secondary: matchScore(eCats, lc),
          fullMark: 100,
        })));

      } catch (err: any) {
        if (!axios.isCancel(err)) {
          console.error("[School fetch]:", err);
          setSchoolOnlyScores(FALLBACK_BENCH_CATS);
          setEnsightlyScores(FALLBACK_BENCH_CATS);
          setSchoolOrbitData(SCHOOL_LIFE_CIRCLES.map(lc => ({ subject: lc, primary: 65, secondary: 70, fullMark: 100 })));
        }
      } finally {
        if (!controller.signal.aborted) setSchoolLoading(false);
      }
    };

    fetchSchoolData();
    return () => controller.abort();
  }, [selectedChildId, selectedPeriod, schoolSolidScope, schoolBenchmarkScope, schoolBenchmarkGranularity, schoolBenchmarkPeerGrade, schoolCountry, schoolState, schoolCity, schoolNationwideBoard]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derived state
  const activeChild = students.find(s => s.id === selectedChildId) || students[0];
  const activeChildName = activeChild ? `${activeChild.firstName} ${activeChild.lastName}` : "Student";
  const siblings = students.filter(s => s.id !== selectedChildId);
  const schoolName = activeChild?.school?.name || "School";
  const childGrade = activeChild?.grade ? Number(activeChild.grade) : null;

  const schoolSolidName = useMemo(() => {
    if (schoolSolidScope === "school") return schoolName;
    if (schoolSolidScope === "my_board") return `Board (${activeChild?.boardName || activeChild?.school?.boardName || 'Same'})`;
    if (schoolSolidScope === "my_state") return `State (${activeChild?.school?.state || 'Same'})`;
    if (schoolSolidScope === "my_city") return `City (${activeChild?.school?.city || 'Same'})`;
    return schoolName;
  }, [schoolSolidScope, schoolName, activeChild]);


  // ── Peer school definitions ─────────────────────────────────────

  const PEER_SCHOOLS = [
    { key: "dps", label: "Delhi Public School" },
    { key: "giis", label: "Global Indian Intl School" },
  ];

  // ── Available grades 1–12 with child's grade marked ───────────────────
  const gradeOptions = useMemo(() => [
    { value: "all", label: "All Grades", rec: false },
    ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(g => ({
      value: String(g),
      label: `Grade ${g}`,
      rec: g === childGrade,
    })),
  ], [childGrade]);

  // ── Derive human-readable benchmark label from 3-tier state ──────────────
  const benchmarkName = useMemo(() => {
    if (benchmarkScope === "none") return "No comparison selected";
    if (benchmarkScope === "sibling") {
      const sib = students.find(s => s.id === Number(benchmarkGranularity));
      return sib ? `${sib.firstName} ${sib.lastName}` : "Sibling";
    }
    const gradeLabel = benchmarkGranularity !== "all" ? ` • Grade ${benchmarkGranularity}` : " • All Grades";
    if (benchmarkScope === "school") return `${schoolName}${gradeLabel}`;
    if (benchmarkScope === "nationwide") return `Nationwide${gradeLabel}`;
    if (benchmarkScope === "my_board") return `Board (${activeChild?.boardName || activeChild?.school?.boardName})${gradeLabel}`;
    if (benchmarkScope === "my_state") return `State (${activeChild?.school?.state})${gradeLabel}`;
    if (benchmarkScope === "my_city") return `City (${activeChild?.school?.city})${gradeLabel}`;
    if (benchmarkScope === "other_school") {
      const ps = PEER_SCHOOLS.find(p => p.key === benchmarkGranularity);
      const gradeL = benchmarkPeerGrade !== "all" ? ` • Grade ${benchmarkPeerGrade}` : " • All Grades";
      return ps ? `${ps.label}${gradeL}` : "Peer School";
    }
    return "Benchmark";
  }, [benchmarkScope, benchmarkGranularity, benchmarkPeerGrade, students, schoolName, childGrade, activeChild]);

  const schoolBenchmarkName = useMemo(() => {
    if (schoolBenchmarkScope === "none") return "No comparison selected";
    if (schoolBenchmarkScope === "nationwide") return "Nationwide Average";
    if (schoolBenchmarkScope === "my_board") return `Board (${activeChild?.boardName || activeChild?.school?.boardName}) Average`;
    if (schoolBenchmarkScope === "my_state") return `State (${activeChild?.school?.state}) Average`;
    if (schoolBenchmarkScope === "my_city") return `City (${activeChild?.school?.city}) Average`;
    if (schoolBenchmarkScope === "other_school") {
      const ps = PEER_SCHOOLS.find(p => p.key === schoolBenchmarkGranularity);
      const gradeL = schoolBenchmarkPeerGrade !== "all" ? ` • Grade ${schoolBenchmarkPeerGrade}` : " • All Grades";
      return ps ? `${ps.label}${gradeL}` : "Peer School";
    }
    return "Nationwide Average";
  }, [schoolBenchmarkScope, schoolBenchmarkGranularity, schoolBenchmarkPeerGrade, activeChild]);

  // ── When scope changes: smart-reset sub-filters ────────────────────────────
  const handleScopeChange = (scope: BenchmarkScope) => {
    if (benchmarkScope === scope) {
      setBenchmarkScope("none");
      return;
    }
    
    setBenchmarkScope(scope);
    if (scope === "school" || scope === "nationwide" || scope === "my_board" || scope === "my_state" || scope === "my_city") {
      setBenchmarkGranularity("all");
      setBenchmarkPeerGrade("all");
    } else if (scope === "other_school") {
      setBenchmarkGranularity(""); // no school selected yet — grade stays locked
      setBenchmarkPeerGrade(childGrade ? String(childGrade) : "all");
    } else if (scope === "sibling" && siblings.length > 0) {
      setBenchmarkGranularity(String(siblings[0].id));
      setBenchmarkPeerGrade("all");
    }
  };

  const handleSchoolScopeChange = (scope: BenchmarkScope) => {
    if (schoolBenchmarkScope === scope) {
      setSchoolBenchmarkScope("none");
      return;
    }

    setSchoolBenchmarkScope(scope);
    if (scope === "nationwide" || scope === "my_board" || scope === "my_state" || scope === "my_city") { setSchoolBenchmarkGranularity("all"); setSchoolBenchmarkPeerGrade("all"); }
    else if (scope === "other_school") { setSchoolBenchmarkGranularity(""); setSchoolBenchmarkPeerGrade(childGrade ? String(childGrade) : "all"); }
  };

  const overallScore = useMemo(() => {
    if (childScores.length === 0) return 0;
    return Math.round(childScores.reduce((a, c) => a + c.score, 0) / childScores.length);
  }, [childScores]);

  const overallBenchmarkScore = useMemo(() => {
    if (benchmarkScores.length === 0) return 0;
    return Math.round(benchmarkScores.reduce((a, c) => a + c.score, 0) / benchmarkScores.length);
  }, [benchmarkScores]);

  const scoreDelta = overallScore - overallBenchmarkScore;

  // School Tab Derived Data
  const schoolOverallScore = useMemo(() => {
    if (schoolOnlyScores.length === 0) return 0;
    return Math.round(schoolOnlyScores.reduce((a, c) => a + c.score, 0) / schoolOnlyScores.length);
  }, [schoolOnlyScores]);

  const ensightlyOverallScore = useMemo(() => {
    if (ensightlyScores.length === 0) return 0;
    return Math.round(ensightlyScores.reduce((a, c) => a + c.score, 0) / ensightlyScores.length);
  }, [ensightlyScores]);

  const schoolScoreDelta = schoolOverallScore - ensightlyOverallScore;

  const childInsight = useMemo(() => generateWellbeingInsight(orbitData, activeChildName, false), [orbitData, activeChildName]);
  const schoolInsight = useMemo(() => generateWellbeingInsight(schoolOrbitData, schoolName, true), [schoolOrbitData, schoolName]);

  const strongestCategory = [...childScores].sort((a, b) => b.score - a.score)[0];
  const weakestCategory = [...childScores].sort((a, b) => a.score - b.score)[0];

  if (loading && students.length === 0) {
    return (
      <div className="min-h-screen bg-[#F6F8F9] flex items-center justify-center">
        <FiLoader className="text-[#1ecab8] animate-spin" size={32} />
      </div>
    );
  }

  if (!loading && students.length === 0) {
    return (
      <div className="min-h-screen bg-[#F6F8F9] flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-2xl shadow-sm border border-slate-200">
          <FiUser className="mx-auto text-[#1ecab8] mb-4" size={48} />
          <h2 className="text-xl font-black text-slate-800 mb-2">No Students Found</h2>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">
            We couldn't find any student records linked to your account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F8F9] text-slate-800 font-sans pb-24">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-10">
        
        {/* 1. PREMIUM HEADER */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-[10px] font-bold text-[#1ecab8] uppercase tracking-[0.18em] mb-2">Wellbeing Intelligence</p>
            <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-2">Your child's wellbeing</h1>
            <p className="text-sm text-slate-500 max-w-xl">
              A holistic view of how your child is doing across the important circles of everyday life.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-400 bg-white/60 px-3 py-1.5 rounded-full border border-slate-200">
            <div className="w-1.5 h-1.5 rounded-full bg-[#1ecab8] animate-pulse" />
            Updated recently
          </div>
        </div>

        {/* TABS NAVIGATION */}
        <div className="flex bg-white rounded-t-2xl border-b-2 border-slate-100 overflow-hidden mb-6">
          <div 
            onClick={() => setActiveTab("child")}
            className={`flex-1 p-5 cursor-pointer transition-colors duration-200 border-b-2 ${
              activeTab === "child" 
                ? "bg-[#1ecab8]/5 border-[#1ecab8]" 
                : "border-transparent hover:bg-slate-50"
            }`}
          >
            <h3 className={`text-sm font-black uppercase tracking-widest mb-1 ${activeTab === "child" ? "text-[#1ecab8]" : "text-slate-400"}`}>
              Child Wellbeing Analysis
            </h3>
            <p className={`text-xs ${activeTab === "child" ? "text-slate-600" : "text-slate-400"}`}>
              {activeChildName} vs {benchmarkName}
            </p>
          </div>
          <div 
            onClick={() => setActiveTab("school")}
            className={`flex-1 p-5 cursor-pointer transition-colors duration-200 border-b-2 ${
              activeTab === "school" 
                ? "bg-[#1ecab8]/5 border-[#1ecab8]" 
                : "border-transparent hover:bg-slate-50"
            }`}
          >
            <h3 className={`text-sm font-black uppercase tracking-widest mb-1 ${activeTab === "school" ? "text-[#1ecab8]" : "text-slate-400"}`}>
              School Wellbeing Analysis
            </h3>
            <p className={`text-xs ${activeTab === "school" ? "text-slate-600" : "text-slate-400"}`}>
              {schoolName} vs {schoolBenchmarkName}
            </p>
          </div>
        </div>

        {/* --- CHILD TAB CONTENT --- */}
        {activeTab === "child" && (
          <>
          {/* ZONE RINGS LEGEND */}
          <div className="flex flex-wrap items-center gap-4 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 px-2">
            <span>Zone Rings:</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#3b82f6]" /> Blue</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#10b981]" /> Green</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#eab308]" /> Yellow</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#a16207]" /> Brown</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#f87171]" /> Light Red</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#991b1b]" /> Dark Red</span>
            <span className="text-slate-300 ml-2">|</span>
            <span className="ml-2 lowercase tracking-normal font-semibold text-slate-400">← inner = worst • outer = best →</span>
          </div>

          {/* ── CUSTOM COMPARISON — Compact Single Column ────────────────────── */}
          <div className="bg-[#1ecab8]/5 border-l-4 border-[#1ecab8] rounded-r-2xl p-5 mb-5 transition-all">
            <div 
              className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer select-none ${showChildCustomComparison ? 'mb-4' : ''}`}
              onClick={() => setShowChildCustomComparison(!showChildCustomComparison)}
            >
              <div className="flex items-center gap-2">
                <h3 className="text-[11px] font-black text-[#1ecab8] uppercase tracking-widest">Custom Comparison</h3>
                {showChildCustomComparison ? <FiChevronUp className="text-[#1ecab8]" /> : <FiChevronDown className="text-[#1ecab8]" />}
              </div>
              
              {showChildCustomComparison && (
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Time Range:</span>
                  <div className="flex items-center gap-1 bg-slate-200/50 p-1 rounded-xl">
                    {(["week", "month", "quarter", "year", "all_time"] as const).map(p => (
                      <button
                        key={p}
                        onClick={() => setSelectedPeriod(p)}
                        className={`px-3 py-1 rounded-lg text-[10px] font-bold transition-all whitespace-nowrap ${
                          selectedPeriod === p ? "bg-white text-[#1ecab8] shadow-sm" : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        {p === "all_time" ? "All time" : p.charAt(0).toUpperCase() + p.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {showChildCustomComparison && (
              <>
                {/* Solid Line — full-width filter stack */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-5 h-0.5 bg-slate-700 shrink-0" />
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Solid — Primary Focus</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {students.map(s => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedChildId(s.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all duration-200 ${
                      selectedChildId === s.id
                        ? "bg-slate-700 text-white border-slate-700 shadow-sm"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-700"
                    }`}
                  >
                    <FiUser size={11} />
                    {s.firstName} {s.lastName}
                  </button>
                ))}
              </div>
            </div>

            {/* Dashed Line — full-width filter stack */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-5 h-0 border-t-2 border-dashed border-slate-700 shrink-0" />
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Dashed — Compare against</span>
              </div>

              {/* Scope pills */}
              <div className="flex flex-wrap gap-1.5 mb-4">
                {([
                  { key: "school" as BenchmarkScope, label: "School", icon: <FiHome size={11} /> },
                  { key: "nationwide" as BenchmarkScope, label: "Nationwide", icon: <FiGlobe size={11} /> },
                  ...((activeChild?.boardName || activeChild?.school?.boardName) ? [{ key: "my_board" as BenchmarkScope, label: `Board (${activeChild?.boardName || activeChild?.school?.boardName})`, icon: <FiAward size={11} /> }] : []),
                  ...(activeChild?.school?.state ? [{ key: "my_state" as BenchmarkScope, label: `State (${activeChild.school.state})`, icon: <FiMap size={11} /> }] : []),
                  ...(activeChild?.school?.city ? [{ key: "my_city" as BenchmarkScope, label: `City (${activeChild.school.city})`, icon: <FiMapPin size={11} /> }] : []),
                  ...(siblings.length > 0 ? [{ key: "sibling" as BenchmarkScope, label: "Sibling", icon: <FiUsers size={11} /> }] : []),
                ]).map(scope => (
                  <button
                    key={scope.key}
                    onClick={() => handleScopeChange(scope.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all duration-200 ${
                      benchmarkScope === scope.key
                        ? "bg-[#1ecab8] text-white border-[#1ecab8] shadow-sm"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:border-[#1ecab8]/50 hover:text-[#1ecab8]"
                    }`}
                  >
                    {scope.icon}{scope.label}
                  </button>
                ))}
              </div>

              {/* ── Nationwide specific filters ── */}
              
              {/* ── Nationwide specific filters — Cascading Country → State → City ── */}
              {benchmarkScope === "nationwide" && (() => {
                // Build filtered options using string columns (Cities has .state and .country columns)
                const statesForCountry = childCountry === "all"
                  ? dbStates
                  : dbStates.filter((s: any) => s.country === childCountry);
                const citiesForState = childState !== "all"
                  ? dbCities.filter((c: any) => c.state === childState)
                  : childCountry !== "all"
                    ? dbCities.filter((c: any) => c.country === childCountry)
                    : dbCities;

                return (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 mt-4">
                    {/* Country */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Country</label>
                      <CustomDropdown
                        value={childCountry}
                        onChange={(v: string) => {
                          setChildCountry(v);
                          setChildState("all"); // reset dependents
                          setChildCity("all");
                        }}
                        options={[
                          { value: "all", label: "All Countries" },
                          ...dbCountries.map((opt: any) => ({ value: opt.countryName, label: opt.countryName }))
                        ]}
                      />
                    </div>

                    {/* State — filtered by selected country */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">State</label>
                      <CustomDropdown
                        value={childState}
                        onChange={(v: string) => {
                          setChildState(v);
                          setChildCity("all"); // reset city when state changes
                        }}
                        options={[
                          { value: "all", label: childCountry === "all" ? "All States" : `All in ${childCountry}` },
                          ...statesForCountry.map((opt: any) => ({ value: opt.state, label: opt.state }))
                        ]}
                      />
                    </div>

                    {/* City — filtered by selected state (or country) */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">City</label>
                      <CustomDropdown
                        value={childCity}
                        onChange={setChildCity}
                        options={[
                          { value: "all", label: childState === "all" ? "All Cities" : `All in ${childState}` },
                          ...citiesForState.map((opt: any) => ({ value: opt.city, label: opt.city }))
                        ]}
                      />
                    </div>

                    {/* Board — independent filter */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Board</label>
                      <CustomDropdown
                        value={childNationwideBoard}
                        onChange={setChildNationwideBoard}
                        options={[
                          { value: "all", label: "All Boards" },
                          ...dbBoards.map((opt: any) => ({ value: opt.name, label: opt.name }))
                        ]}
                      />
                    </div>
                  </div>
                );
              })()}


              {/* ── School / Nationwide: Grade filter */}
              {(benchmarkScope === "school" || benchmarkScope === "nationwide") && (
                <>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Grade</p>
                  <div className="flex flex-wrap gap-1.5">
                    {gradeOptions.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setBenchmarkGranularity(opt.value)}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all duration-200 ${
                          benchmarkGranularity === opt.value
                            ? "bg-slate-800 text-white border-slate-800"
                            : "bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-400"
                        }`}
                      >
                        {opt.label}
                        {opt.rec && (
                          <span className={`text-[8px] font-black px-1 py-0.5 rounded ${
                            benchmarkGranularity === opt.value ? "bg-white/20" : "bg-[#1ecab8]/15 text-[#1ecab8]"
                          }`}>You</span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}



              {/* ── Sibling picker */}
              {benchmarkScope === "sibling" && siblings.length > 0 && (
                <>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Select Sibling</p>
                  <div className="flex flex-wrap gap-1.5">
                    {siblings.map(s => (
                      <button
                        key={s.id}
                        onClick={() => setBenchmarkGranularity(String(s.id))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all duration-200 ${
                          benchmarkGranularity === String(s.id)
                            ? "bg-slate-800 text-white border-slate-800"
                            : "bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-400"
                        }`}
                      >
                        <FiUsers size={10} />{s.firstName} {s.lastName}
                        <span className="text-[9px] opacity-60">Gr {s.grade || "?"}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
              </>
            )}
          </div>

          {/* ACTIVE SELECTION SUMMARY PILLS */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-2.5 flex items-center gap-2.5 shadow-sm">
              <span className="w-5 h-0.5 bg-slate-700 rounded-full inline-block" />
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Solid:</span>
              <span className="text-sm font-bold text-slate-800">{activeChildName}</span>
            </div>
            <div className="text-slate-300 font-light text-lg">vs</div>
            <div className="bg-[#1ecab8]/10 border border-[#1ecab8]/30 rounded-xl px-4 py-2.5 flex items-center gap-2.5 shadow-sm">
              <span className="w-5 h-0 border-t-2 border-dashed border-[#1ecab8] inline-block" />
              <span className="text-[10px] font-black text-[#1ecab8] uppercase tracking-widest">Dashed:</span>
              <span className="text-sm font-bold text-slate-800">{benchmarkName}</span>
            </div>
          </div>

        {/* SINGLE WHITE CARD: Analytical Report Layout */}
        <div className="bg-white shadow-sm border border-slate-100 rounded-2xl p-6 lg:p-8 mb-8 relative overflow-hidden">
          {/* Loading overlay — shows spinner without destroying layout */}
          {loading && (
            <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-10 rounded-2xl">
              <FiLoader className="text-[#1ecab8] animate-spin" size={36} />
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-8 pb-6 border-b border-slate-100">
            <div>
              <h2 className="text-sm font-black text-slate-800 tracking-widest uppercase">Consolidated Orbit — Categories</h2>
              <p className="text-[10px] text-slate-400 mt-1.5 font-bold uppercase tracking-widest">
                {activeChildName} • {benchmarkName}
              </p>
            </div>
            {/* Legend Right aligned */}
            <div className="flex flex-col gap-2 text-[10px] font-bold text-slate-500 shrink-0">
              <span className="flex items-center gap-2">
                <span className="w-6 h-0.5 bg-slate-600 inline-block rounded-full" />
                {activeChildName}
              </span>
              <span className="flex items-center gap-2">
                <span className="w-6 h-0 border-t-2 border-dashed border-slate-600 inline-block" />
                {benchmarkName}
              </span>
              <span className="text-[9px] text-slate-400 mt-1 tracking-widest uppercase">Outer = Best • Inner = Worst</span>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 items-start">
            
            {/* LEFT: Orbit chart — no separate loading spinner, handled by overlay */}
            <div className="w-full lg:w-[400px] flex flex-col items-center shrink-0">
              <ConsolidatedOrbitChart
                data={orbitData}
                primaryName={activeChildName}
                secondaryName={benchmarkName}
                overallScore={overallScore}
              />
            </div>

            {/* RIGHT: Category table */}
            <div className="flex-1 w-full bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-50 to-slate-50/30 border-b border-slate-100">
                    <th className="px-5 py-3.5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Category</th>
                    <th className="px-5 py-3.5 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">Score</th>
                    <th className="px-5 py-3.5 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">vs Benchmark</th>
                    <th className="px-5 py-3.5 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">Zone</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {LIFE_CIRCLES.map((lc, i) => {
                    const score = childScores.find(c => c.category === lc)?.score ?? 0;
                    const bScore = benchmarkScores.find(c => c.category === lc)?.score ?? 0;
                    const z = getZone(score);
                    const diff = score - bScore;

                    return (
                      <tr
                        key={lc}
                        className="transition-all duration-200 hover:bg-slate-50/50 group"
                      >
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2.5">
                            <div
                              className="w-8 h-8 rounded-xl flex items-center justify-center text-base flex-shrink-0 shadow-sm transition-transform duration-300 group-hover:scale-110"
                              style={{ background: `${z.color}22`, color: z.color }}
                            >
                              {catIcon(lc)}
                            </div>
                            <div>
                              <span className="font-semibold text-gray-800 text-sm">{lc}</span>
                            </div>
                          </div>
                        </td>

                        {/* Score with bar */}
                        <td className="px-5 py-4 text-center">
                          <div className="flex flex-col items-center justify-center gap-2">
                            <span className="font-black text-slate-800 text-sm tracking-tight">{score}<span className="text-[10px] font-bold text-slate-400">/100</span></span>
                            <div className="w-28 h-1.5 rounded-full bg-slate-100 overflow-hidden shadow-inner">
                              <div
                                className="h-full rounded-full transition-all duration-1000 ease-out relative"
                                style={{ width: `${score}%`, background: z.color }}
                              >
                                <div className="absolute top-0 right-0 bottom-0 w-4 bg-white/30 blur-[2px]" />
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* VS Compare diff column */}
                        <td className="px-5 py-4 text-center">
                          <div className="inline-flex items-center justify-center min-w-[3rem] px-2 py-1 rounded-lg bg-slate-50 border border-slate-100">
                            {bScore === 0 && benchmarkScores.every(c => c.score === 0) ? (
                              <span className="text-[11px] font-black tracking-wide text-slate-300">N/A</span>
                            ) : (
                              <span className={`text-[11px] font-black tracking-wide ${diff > 0 ? "text-[#1ecab8]" : diff < 0 ? "text-rose-500" : "text-slate-400"}`}>
                                {diff > 0 ? `+${diff}` : diff}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Zone / Status */}
                        <td className="px-5 py-4 text-center">
                          <span
                            className="inline-block text-[9px] px-3 py-1.5 rounded-md font-black uppercase tracking-[0.2em] shadow-sm"
                            style={{ background: z.color, color: '#fff' }}
                          >
                            {z.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

          </div>
        </div>

        {/* AI WELLBEING INSIGHT (Child Tab) */}
        {(() => {
          const toneStyles = {
            positive: { border: "border-emerald-200", bg: "bg-emerald-50", badge: "bg-emerald-100 text-emerald-700", icon: "text-emerald-500" },
            neutral:  { border: "border-slate-200",   bg: "bg-slate-50",   badge: "bg-slate-100 text-slate-600",   icon: "text-slate-400" },
            caution:  { border: "border-amber-200",   bg: "bg-amber-50",   badge: "bg-amber-100 text-amber-700",   icon: "text-amber-500" },
            alert:    { border: "border-rose-200",    bg: "bg-rose-50",    badge: "bg-rose-100 text-rose-700",     icon: "text-rose-500" },
          };
          const ts = toneStyles[childInsight.tone];
          return (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200/80 overflow-hidden mb-8">
              {/* Header */}
              <div className={`px-5 pt-4 pb-3 border-b ${ts.border} ${ts.bg}`}>
                <div className="flex items-center gap-2">
                  <FiTrendingUp size={16} className={ts.icon} />
                  <span className="text-sm font-bold text-slate-800">AI Wellbeing Insight</span>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ml-auto ${ts.badge}`}>
                    {childInsight.headline}
                  </span>
                </div>
              </div>
              {/* Body */}
              <div className="p-5">
                <p className="text-sm text-slate-600 leading-relaxed mb-4">
                  {childInsight.body}
                </p>
                <div className={`flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100`}>
                  <FiCpu className={`mt-0.5 ${ts.icon}`} size={16} />
                  <p className="text-xs font-semibold text-slate-700">
                    {childInsight.action}
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        {/* INSIGHT STRIP */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="flex items-start gap-4 bg-white border border-emerald-100 rounded-2xl p-5 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-xl shrink-0">🏆</div>
            <div>
              <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1">Strength</p>
              <p className="text-sm font-black text-slate-800">{strongestCategory?.category || "—"}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Score: <span className="font-bold text-emerald-600">{strongestCategory?.score ?? 0}/100</span>
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4 bg-white border border-amber-100 rounded-2xl p-5 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-xl shrink-0">🎯</div>
            <div>
              <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-1">Opportunity</p>
              <p className="text-sm font-black text-slate-800">{weakestCategory?.category || "—"}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Needs a little more attention</p>
            </div>
          </div>
          <div className="flex items-start gap-4 bg-white border border-sky-100 rounded-2xl p-5 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center text-xl shrink-0">
              {scoreDelta >= 0 ? '📈' : '📉'}
            </div>
            <div>
              <p className="text-[10px] font-black text-sky-600 uppercase tracking-widest mb-1">Benchmark</p>
              <p className="text-sm font-black text-slate-800">
                {scoreDelta >= 0 ? '+' : ''}{scoreDelta} pts
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {scoreDelta >= 0 ? 'Above' : 'Below'} {benchmarkName}
              </p>
            </div>
          </div>
        </div>
          </>
        )}

        {/* --- SCHOOL TAB CONTENT --- */}
        {activeTab === "school" && (
          <div className="bg-white rounded-b-2xl rounded-t-none p-6 lg:p-8 mb-8 border-t border-slate-100 shadow-sm relative overflow-hidden">
            {/* Loading overlay — school tab has its own independent loading state */}
            {schoolLoading && (
              <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-10 rounded-2xl">
                <FiLoader className="text-[#1ecab8] animate-spin" size={36} />
              </div>
            )}


            {/* ── CUSTOM COMPARISON FOR SCHOOL — Compact Single Column ────────────────────── */}
            <div className="bg-[#1ecab8]/5 border-l-4 border-[#1ecab8] rounded-r-2xl p-5 mb-5 transition-all">
              <div 
                className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer select-none ${showSchoolCustomComparison ? 'mb-4' : ''}`}
                onClick={() => setShowSchoolCustomComparison(!showSchoolCustomComparison)}
              >
                <div className="flex items-center gap-2">
                  <h3 className="text-[11px] font-black text-[#1ecab8] uppercase tracking-widest">Custom Comparison</h3>
                  {showSchoolCustomComparison ? <FiChevronUp className="text-[#1ecab8]" /> : <FiChevronDown className="text-[#1ecab8]" />}
                </div>
                
                {showSchoolCustomComparison && (
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Time Range:</span>
                    <div className="flex items-center gap-1 bg-slate-200/50 p-1 rounded-xl">
                      {(["week", "month", "quarter", "year", "all_time"] as const).map(p => (
                        <button
                          key={p}
                          onClick={() => setSelectedPeriod(p)}
                          className={`px-3 py-1 rounded-lg text-[10px] font-bold transition-all whitespace-nowrap ${
                            selectedPeriod === p ? "bg-white text-[#1ecab8] shadow-sm" : "text-slate-500 hover:text-slate-700"
                          }`}
                        >
                          {p === "all_time" ? "All time" : p.charAt(0).toUpperCase() + p.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {showSchoolCustomComparison && (
                <>
                  {/* Solid Line — full-width filter stack */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-5 h-0.5 bg-slate-700 shrink-0" />
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Solid — Primary Focus</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {([
                    { key: "school" as SolidScope, label: "School", icon: <FiHome size={11} /> },
                    { key: "my_board" as SolidScope, label: `Board ${activeChild?.boardName || activeChild?.school?.boardName ? '(' + (activeChild?.boardName || activeChild?.school?.boardName) + ')' : ''}`, icon: <FiAward size={11} /> },
                    { key: "my_state" as SolidScope, label: `State ${activeChild?.school?.state ? '(' + activeChild.school.state + ')' : ''}`, icon: <FiMap size={11} /> },
                    { key: "my_city" as SolidScope, label: `City ${activeChild?.school?.city ? '(' + activeChild.school.city + ')' : ''}`, icon: <FiMapPin size={11} /> },
                  ]).map(scope => (
                    <button
                      key={scope.key}
                      onClick={() => setSchoolSolidScope(scope.key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all duration-200 ${
                        schoolSolidScope === scope.key
                          ? "bg-slate-700 text-white border-slate-700 shadow-sm"
                          : "bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-700"
                      }`}
                    >
                      {scope.icon}{scope.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dashed Line — full-width filter stack */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-5 h-0 border-t-2 border-dashed border-slate-700 shrink-0" />
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Dashed — Compare against</span>
                </div>

                {/* Level 1: Scope Pills */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {([
                    { key: "nationwide" as BenchmarkScope, label: "Nationwide", icon: <FiGlobe size={11} /> },
                    ...((activeChild?.boardName || activeChild?.school?.boardName) ? [{ key: "my_board" as BenchmarkScope, label: `Board (${activeChild?.boardName || activeChild?.school?.boardName})`, icon: <FiAward size={11} /> }] : []),
                    ...(activeChild?.school?.state ? [{ key: "my_state" as BenchmarkScope, label: `State (${activeChild.school.state})`, icon: <FiMap size={11} /> }] : []),
                    ...(activeChild?.school?.city ? [{ key: "my_city" as BenchmarkScope, label: `City (${activeChild.school.city})`, icon: <FiMapPin size={11} /> }] : []),
                    { key: "other_school" as BenchmarkScope, label: "Peer School", icon: <FiMapPin size={11} /> },
                  ]).map(scope => (
                    <button
                      key={scope.key}
                      onClick={() => handleSchoolScopeChange(scope.key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all duration-200 ${
                        schoolBenchmarkScope === scope.key
                          ? "bg-[#1ecab8] text-white border-[#1ecab8] shadow-sm"
                          : "bg-slate-50 text-slate-600 border-slate-200 hover:border-[#1ecab8]/50 hover:text-[#1ecab8]"
                      }`}
                    >
                      {scope.icon}{scope.label}
                    </button>
                  ))}
                </div>

                {/* ── Nationwide specific filters — Cascading Country → State → City ── */}
                {schoolBenchmarkScope === "nationwide" && (() => {
                  // Build filtered options using string columns (Cities has .state and .country columns)
                  const schoolStatesForCountry = schoolCountry === "all"
                    ? dbStates
                    : dbStates.filter((s: any) => s.country === schoolCountry);
                  const schoolCitiesForState = schoolState !== "all"
                    ? dbCities.filter((c: any) => c.state === schoolState)
                    : schoolCountry !== "all"
                      ? dbCities.filter((c: any) => c.country === schoolCountry)
                      : dbCities;

                  return (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 mt-4">
                      {/* Country */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Country</label>
                        <CustomDropdown
                          value={schoolCountry}
                          onChange={(v: string) => {
                            setSchoolCountry(v);
                            setSchoolState("all");
                            setSchoolCity("all");
                          }}
                          options={[
                            { value: "all", label: "All Countries" },
                            ...dbCountries.map((opt: any) => ({ value: opt.countryName, label: opt.countryName }))
                          ]}
                        />
                      </div>

                      {/* State — filtered by selected country */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">State</label>
                        <CustomDropdown
                          value={schoolState}
                          onChange={(v: string) => {
                            setSchoolState(v);
                            setSchoolCity("all");
                          }}
                          options={[
                            { value: "all", label: schoolCountry === "all" ? "All States" : `All in ${schoolCountry}` },
                            ...schoolStatesForCountry.map((opt: any) => ({ value: opt.state, label: opt.state }))
                          ]}
                        />
                      </div>

                      {/* City — filtered by selected state */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">City</label>
                        <CustomDropdown
                          value={schoolCity}
                          onChange={setSchoolCity}
                          options={[
                            { value: "all", label: schoolState === "all" ? "All Cities" : `All in ${schoolState}` },
                            ...schoolCitiesForState.map((opt: any) => ({ value: opt.city, label: opt.city }))
                          ]}
                        />
                      </div>

                      {/* Board — independent */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Board</label>
                        <CustomDropdown
                          value={schoolNationwideBoard}
                          onChange={setSchoolNationwideBoard}
                          options={[
                            { value: "all", label: "All Boards" },
                            ...dbBoards.map((opt: any) => ({ value: opt.name, label: opt.name }))
                          ]}
                        />
                      </div>
                    </div>
                  );
                })()}



                {/* ── Peer School: School picker → Grade filter ─── */}
                {schoolBenchmarkScope === "other_school" && (
                  <>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Select School</p>
                    <div className={`flex flex-wrap gap-1.5 ${schoolBenchmarkGranularity ? "mb-4" : ""}`}>
                      {PEER_SCHOOLS.map(ps => (
                        <button
                          key={ps.key}
                          onClick={() => setSchoolBenchmarkGranularity(ps.key)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all duration-200 ${
                            schoolBenchmarkGranularity === ps.key
                              ? "bg-slate-800 text-white border-slate-800"
                              : "bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-400"
                          }`}
                        >
                          <FiMapPin size={10} />{ps.label}
                        </button>
                      ))}
                    </div>
                    {/* Grade unlocks after peer school is selected */}
                    {schoolBenchmarkGranularity && (
                      <>
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Grade</p>
                        <div className="flex flex-wrap gap-1.5">
                          {gradeOptions.map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => setSchoolBenchmarkPeerGrade(opt.value)}
                              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all duration-200 ${
                                schoolBenchmarkPeerGrade === opt.value
                                  ? "bg-slate-800 text-white border-slate-800"
                                  : "bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-400"
                              }`}
                            >
                              {opt.label}
                              {opt.rec && (
                                <span className={`text-[8px] font-black px-1 py-0.5 rounded ${
                                  schoolBenchmarkPeerGrade === opt.value ? "bg-white/20" : "bg-[#1ecab8]/15 text-[#1ecab8]"
                                }`}>You</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
                </>
              )}
            </div>


            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-8 pb-6 border-b border-slate-100">
              <div>
                <h2 className="text-sm font-black text-slate-800 tracking-widest uppercase">Consolidated Orbit — Categories</h2>
                <p className="text-[10px] text-slate-400 mt-1.5 font-bold uppercase tracking-widest">
                  {schoolName} • {schoolBenchmarkName}
                </p>
              </div>
              {/* Legend Right aligned */}
              <div className="flex flex-col gap-2 text-[10px] font-bold text-slate-500 shrink-0">
                <span className="flex items-center gap-2">
                  <span className="w-6 h-0.5 bg-slate-600 inline-block rounded-full" />
                  {schoolName}
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-6 h-0 border-t-2 border-dashed border-slate-600 inline-block" />
                  {schoolBenchmarkScope === "none" ? "None" : schoolBenchmarkName}
                </span>
                <span className="text-[9px] text-slate-400 mt-1 tracking-widest uppercase">Outer = Best • Inner = Worst</span>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-8 items-start mb-8">
              {/* LEFT: Orbit chart */}
              <div className="w-full lg:w-[400px] flex flex-col items-center shrink-0">
                <ConsolidatedOrbitChart
                  data={schoolOrbitData}
                  primaryName={schoolName}
                  secondaryName={schoolBenchmarkName}
                  overallScore={schoolOverallScore}
                />
              </div>

              {/* RIGHT: Category table */}
              <div className="flex-1 w-full bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gradient-to-r from-slate-50 to-slate-50/30 border-b border-slate-100">
                      <th className="px-5 py-3.5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Category</th>
                      <th className="px-5 py-3.5 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">Score</th>
                      <th className="px-5 py-3.5 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">vs Nationwide</th>
                      <th className="px-5 py-3.5 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">Zone</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {SCHOOL_LIFE_CIRCLES.map(lc => {
                      const score = matchScore(schoolOnlyScores, lc);
                      const bench = matchScore(ensightlyScores, lc);
                      const diff = score - bench;
                      const z = getZone(score);

                      return (
                        <tr 
                          key={lc}
                          className="transition-all duration-200 hover:bg-slate-50/50 group"
                        >
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-2.5">
                              <div
                                className="w-8 h-8 rounded-xl flex items-center justify-center text-base flex-shrink-0 shadow-sm transition-transform duration-300 group-hover:scale-110"
                                style={{ background: `${z.color}22`, color: z.color }}
                              >
                                {catIcon(lc)}
                              </div>
                              <div>
                                <span className="font-semibold text-gray-800 text-sm">{lc}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <div className="flex flex-col items-center gap-1.5">
                              <span className="font-bold text-slate-800 text-[13px]">{score}<span className="text-[10px] text-slate-400 font-semibold">/100</span></span>
                              <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${score}%`, backgroundColor: z.color }} />
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-md text-[11px] font-bold ${
                              diff > 0 ? "bg-emerald-50 text-emerald-600" : diff < 0 ? "bg-rose-50 text-rose-600" : "bg-slate-50 text-slate-500"
                            }`}>
                              {diff > 0 ? `+${diff}` : diff}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <span 
                              className="inline-block px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest text-white shadow-sm"
                              style={{ backgroundColor: z.color }}
                            >
                              {z.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* AI WELLBEING INSIGHT (School Tab) */}
            {(() => {
              const toneStyles = {
                positive: { border: "border-emerald-200", bg: "bg-emerald-50", badge: "bg-emerald-100 text-emerald-700", icon: "text-emerald-500" },
                neutral:  { border: "border-slate-200",   bg: "bg-slate-50",   badge: "bg-slate-100 text-slate-600",   icon: "text-slate-400" },
                caution:  { border: "border-amber-200",   bg: "bg-amber-50",   badge: "bg-amber-100 text-amber-700",   icon: "text-amber-500" },
                alert:    { border: "border-rose-200",    bg: "bg-rose-50",    badge: "bg-rose-100 text-rose-700",     icon: "text-rose-500" },
              };
              const ts = toneStyles[schoolInsight.tone];
              return (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200/80 overflow-hidden mb-8">
                  {/* Header */}
                  <div className={`px-5 pt-4 pb-3 border-b ${ts.border} ${ts.bg}`}>
                    <div className="flex items-center gap-2">
                      <FiTrendingUp size={16} className={ts.icon} />
                      <span className="text-sm font-bold text-slate-800">AI Wellbeing Insight</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ml-auto ${ts.badge}`}>
                        {schoolInsight.headline}
                      </span>
                    </div>
                  </div>
                  {/* Body */}
                  <div className="p-5">
                    <p className="text-sm text-slate-600 leading-relaxed mb-4">
                      {schoolInsight.body}
                    </p>
                    <div className={`flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100`}>
                      <FiCpu className={`mt-0.5 ${ts.icon}`} size={16} />
                      <p className="text-xs font-semibold text-slate-700">
                        {schoolInsight.action}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* INSIGHT STRIP */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-4 bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-xl shrink-0">
                  <FiGlobe className="text-slate-400" />
                </div>
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">School Overall Score</p>
                  <p className="text-sm font-black text-slate-800">{schoolOverallScore}/100</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Aggregated wellbeing index</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-white border border-sky-100 rounded-2xl p-5 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center text-xl shrink-0">
                  {schoolScoreDelta >= 0 ? '📈' : '📉'}
                </div>
                <div>
                  <p className="text-[10px] font-black text-sky-600 uppercase tracking-widest mb-1">vs Nationwide</p>
                  <p className="text-sm font-black text-slate-800">
                    {schoolScoreDelta >= 0 ? '+' : ''}{schoolScoreDelta} pts
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {schoolScoreDelta >= 0 ? 'Above' : 'Below'} the national average
                  </p>
                </div>
              </div>
            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default ParentWellbeing;

